import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Printer, Save, Plus, Trash2, Edit3 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY = {
  name: "Charlesworth Club",
  address: "Pippins Roost, Walliswood, RH5 5RD",
  email: "noah@charlesworth.club",
  sort_code: "04-00-03",
  account_number: "55298670",
  account_name: "Charlesworth Club"
};

const InvoiceEditor = () => {
  const { bookingId } = useParams();
  const [invoice, setInvoice] = useState({
    invoice_number: "",
    date: new Date().toISOString().split("T")[0],
    due_date: "",
    customer_name: "",
    customer_email: "",
    customer_address: "",
    event_date: "",
    items: [],
    deposit_paid: 450,
    notes: "",
    status: "pending"
  });
  const [loading, setLoading] = useState(!!bookingId);

  useEffect(() => {
    if (bookingId) {
      loadBookingData();
    } else {
      // Fresh invoice
      const count = Math.floor(Math.random() * 9000) + 1000;
      const now = new Date();
      setInvoice(prev => ({
        ...prev,
        invoice_number: `CC-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}-${count}`,
        date: now.toISOString().split("T")[0]
      }));
    }
  }, [bookingId]);

  const loadBookingData = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        setLoading(false);
        return;
      }

      // Try to load existing invoice first
      const invoiceRes = await axios.get(`${API}/invoices/booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: null }));

      if (invoiceRes.data) {
        const inv = invoiceRes.data;
        setInvoice({
          invoice_number: inv.invoice_number || "",
          date: inv.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
          due_date: inv.due_date || "",
          customer_name: inv.customer_name || "",
          customer_email: inv.customer_email || "",
          customer_address: "",
          event_date: inv.event_date || "",
          items: [
            { description: inv.package_name || "Package", amount: inv.package_price || 0 },
            ...(inv.addons_total > 0 ? [{ description: "Add-ons", amount: inv.addons_total }] : [])
          ],
          deposit_paid: inv.deposit_paid || 450,
          notes: "",
          status: inv.status || "pending"
        });
      } else {
        // Load from booking
        const bookingsRes = await axios.get(`${API}/bookings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const booking = bookingsRes.data.find(b => b.id === bookingId);
        if (booking) {
          const eventDate = new Date(booking.event_date);
          const dueDate = new Date(eventDate);
          dueDate.setDate(dueDate.getDate() - 14);
          const now = new Date();
          const count = await axios.get(`${API}/invoices`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.data.length + 1).catch(() => 1);

          const items = [{ description: booking.package_name, amount: booking.package_price }];
          if (booking.selected_addons?.length > 0) {
            booking.selected_addons.forEach(addon => {
              items.push({ description: addon.label || "Add-on", amount: addon.price || 0 });
            });
          }

          setInvoice({
            invoice_number: `CC-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}-${String(count).padStart(4, "0")}`,
            date: now.toISOString().split("T")[0],
            due_date: dueDate.toISOString().split("T")[0],
            customer_name: booking.customer_name || "",
            customer_email: booking.customer_email || "",
            customer_address: booking.venue_address || "",
            event_date: booking.event_date || "",
            items,
            deposit_paid: booking.deposit_amount || 450,
            notes: "",
            status: "pending"
          });
        }
      }
    } catch (error) {
      console.error("Error loading booking:", error);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { description: "", amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: field === "amount" ? parseFloat(value) || 0 : value } : item)
    }));
  };

  const updateField = (field, value) => {
    setInvoice(prev => ({ ...prev, [field]: value }));
  };

  const subtotal = invoice.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const balanceDue = subtotal - (parseFloat(invoice.deposit_paid) || 0);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">Loading invoice...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toolbar - hidden in print */}
      <div className="print:hidden bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Edit3 size={18} className="text-yellow-400" />
            <span className="text-white font-semibold text-sm">Invoice Editor</span>
            <span className="text-neutral-500 text-xs">— Charlesworth Club</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-2 px-5 text-sm uppercase tracking-wider flex items-center gap-2 transition-colors"
              data-testid="print-invoice"
            >
              <Printer size={16} />
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Document */}
      <div className="max-w-4xl mx-auto my-8 print:my-0 print:max-w-none">
        <div className="bg-white shadow-lg print:shadow-none p-10 print:p-8" id="invoice-document" data-testid="invoice-document">
          {/* Header */}
          <div className="flex justify-between items-start mb-10 border-b-2 border-gray-900 pb-8">
            <div>
              <h1
                className="text-3xl font-black text-gray-900 mb-1"
                contentEditable
                suppressContentEditableWarning
                data-testid="invoice-company-name"
              >
                {COMPANY.name}
              </h1>
              <p className="text-gray-500 text-sm" contentEditable suppressContentEditableWarning>{COMPANY.address}</p>
              <p className="text-gray-500 text-sm" contentEditable suppressContentEditableWarning>{COMPANY.email}</p>
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">INVOICE</h2>
              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-end gap-2">
                  <span className="text-gray-500">Invoice #:</span>
                  <input
                    type="text"
                    value={invoice.invoice_number}
                    onChange={(e) => updateField("invoice_number", e.target.value)}
                    className="text-right font-mono font-bold text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent w-48 print:border-none"
                    data-testid="invoice-number-input"
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-gray-500">Date:</span>
                  <input
                    type="date"
                    value={invoice.date}
                    onChange={(e) => updateField("date", e.target.value)}
                    className="text-right text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent print:border-none"
                    data-testid="invoice-date-input"
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-gray-500">Due Date:</span>
                  <input
                    type="date"
                    value={invoice.due_date}
                    onChange={(e) => updateField("due_date", e.target.value)}
                    className="text-right text-gray-900 font-semibold border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent print:border-none"
                    data-testid="invoice-due-date-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="grid grid-cols-2 gap-8 mb-10">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Bill To</h3>
              <input
                type="text"
                value={invoice.customer_name}
                onChange={(e) => updateField("customer_name", e.target.value)}
                placeholder="Customer Name"
                className="block w-full text-gray-900 font-semibold text-lg border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent mb-1 print:border-none"
                data-testid="invoice-customer-name"
              />
              <input
                type="text"
                value={invoice.customer_email}
                onChange={(e) => updateField("customer_email", e.target.value)}
                placeholder="Email"
                className="block w-full text-gray-500 text-sm border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent mb-1 print:border-none"
                data-testid="invoice-customer-email"
              />
              <input
                type="text"
                value={invoice.customer_address}
                onChange={(e) => updateField("customer_address", e.target.value)}
                placeholder="Address"
                className="block w-full text-gray-500 text-sm border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent print:border-none"
                data-testid="invoice-customer-address"
              />
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Event Details</h3>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-gray-500 text-sm w-16">Date:</span>
                <input
                  type="date"
                  value={invoice.event_date}
                  onChange={(e) => updateField("event_date", e.target.value)}
                  className="text-gray-900 font-semibold border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent print:border-none"
                  data-testid="invoice-event-date"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-8">
            <table className="w-full" data-testid="invoice-items-table">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-widest text-gray-400">Description</th>
                  <th className="text-right py-3 text-xs font-bold uppercase tracking-widest text-gray-400 w-32">Amount</th>
                  <th className="w-10 print:hidden"></th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} className="border-b border-gray-200" data-testid={`invoice-item-${index}`}>
                    <td className="py-3">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                        placeholder="Item description"
                        className="w-full text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent print:border-none"
                      />
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end">
                        <span className="text-gray-500 mr-1">£</span>
                        <input
                          type="number"
                          value={item.amount || ""}
                          onChange={(e) => updateItem(index, "amount", e.target.value)}
                          placeholder="0"
                          className="w-24 text-right text-gray-900 font-semibold border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent print:border-none"
                        />
                      </div>
                    </td>
                    <td className="py-3 text-center print:hidden">
                      <button onClick={() => removeItem(index)} className="text-red-400 hover:text-red-600 p-1">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button
              onClick={addItem}
              className="mt-3 text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 print:hidden"
              data-testid="add-invoice-item"
            >
              <Plus size={14} /> Add line item
            </button>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-10">
            <div className="w-72">
              <div className="flex justify-between py-2 text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900 font-semibold">£{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 text-sm items-center">
                <span className="text-gray-500">Deposit Paid</span>
                <div className="flex items-center">
                  <span className="text-green-600 mr-1">-£</span>
                  <input
                    type="number"
                    value={invoice.deposit_paid || ""}
                    onChange={(e) => updateField("deposit_paid", e.target.value)}
                    className="w-20 text-right text-green-600 font-semibold border-b border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent print:border-none"
                    data-testid="invoice-deposit-input"
                  />
                </div>
              </div>
              <div className="flex justify-between py-3 border-t-2 border-gray-900">
                <span className="text-gray-900 font-bold text-lg">Balance Due</span>
                <span className="text-gray-900 font-black text-xl" data-testid="invoice-balance-due">£{balanceDue.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-gray-50 p-6 mb-8 print:bg-gray-100">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Payment Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-400 text-xs mb-1">Account Name</p>
                <p className="text-gray-900 font-semibold" contentEditable suppressContentEditableWarning>{COMPANY.account_name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Sort Code</p>
                <p className="text-gray-900 font-semibold font-mono" contentEditable suppressContentEditableWarning>{COMPANY.sort_code}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Account Number</p>
                <p className="text-gray-900 font-semibold font-mono" contentEditable suppressContentEditableWarning>{COMPANY.account_number}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Reference</p>
                <p className="text-gray-900 font-semibold font-mono">{invoice.invoice_number}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Notes</h3>
            <textarea
              value={invoice.notes}
              onChange={(e) => updateField("notes", e.target.value)}
              placeholder="Add any notes here..."
              rows={3}
              className="w-full text-gray-600 text-sm border border-transparent hover:border-gray-300 focus:border-yellow-400 outline-none bg-transparent resize-none print:border-none"
              data-testid="invoice-notes"
            />
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 text-center">
            <p className="text-gray-400 text-xs" contentEditable suppressContentEditableWarning>
              Thank you for your business — Charlesworth Club
            </p>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:my-0 { margin-top: 0 !important; margin-bottom: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:p-8 { padding: 2rem !important; }
          .print\\:border-none { border-color: transparent !important; }
          .print\\:bg-gray-100 { background-color: #f3f4f6 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          input, textarea { border: none !important; }
          input::-webkit-calendar-picker-indicator { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default InvoiceEditor;
